import { InfoCircleOutlined } from '@ant-design/icons';
import { Card, Input, Select, Spin, Switch, Tag, Tooltip } from 'antd';
import { useFormik } from 'formik';
import { isNil } from 'lodash';
import get from 'lodash/get';
import { observer } from 'mobx-react';
import { Moment } from 'moment';
import React, { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';
import {
    CheckboxForm,
    CheckboxItemError,
} from '../../components/checkboxes-input';
import { CmsUserHistoryBar } from '../../components/cms-user-history-bar';
import { DeleteButton, SaveButton } from '../../components/action-buttons';
import { ErrorAwareLabel } from '../../components/error-aware-label';
import { FeatureFlagSelect } from '../../components/feature-flag-select';
import { GameForm } from '../../components/shared-game-components/game-form';
import { Images } from '../../components/shared-game-components/game-images';
import { IsActiveSwitch } from '../../components/is-active-switch';
import { NotificationManager } from '../../components/notification-manager';
import { PageTopBar } from '../../components/page-top-bar';
import { Section } from '../../components/form-fields/section';
import { Brand, DeepLinkSection } from '../../components/deep-link-section';
import { useDeepLinkVisibility } from '../../components/deep-link-section/use-deep-link-visibility';
import type { DeepLink } from '../../components/deep-link-section';
import { BackgroundColorPicker } from '../../components/background-color-picker';
import { BACKGROUND_COLORS } from '../../components/background-color-picker/constants';
import { localizeLanguage } from '../../utils/languages';
import {
    Acvt,
    AcvtCanvas,
    AcvtGamePage,
} from '../../components/advanced-ct-visualisation-tool';
import {
    PreviewBottomTabBar,
    PreviewProvider,
    PreviewThemeProvider,
    PreviewTabKey,
} from '../../components/preview';
import { CommonScheduleSection } from '../../components/shared-game-components/game-schedule';
import {
    useAuthStore,
    useConfigStore,
    useGamesStore,
    usePromotionStore,
} from '../../contexts/mobx';
import { useUnsavedChanges } from '../../contexts/unsaved-changes';
import { SupportedLanguages } from '../../types';
import { AcvtSupportedLanguages } from '../../types/acvt';
import { ApplicationTargetEnum } from '../../types/app-selection';
import {
    ExternalGame,
    ExternalGameForm,
    ExternalGameRequestCms,
    GameType,
    initialExternalGameForm,
    LABATT_PROMOTION_MAX_LEVEL,
    LABATT_PROMOTION_MIN_LEVEL,
    WebViewAuthentication,
} from '../../types/games';
import { Text } from '../../utils/text';
import {
    convertLocalToUtcString,
    convertUtcToLocalMoment,
} from '../../utils/timezone';
import { useNavigation } from '../../utils/use-navigation';
import { usePlacementLinking } from '../../utils/use-placement-linking';
import style from './style.module.scss';

const convertFlatLabattPromotionsArrayToStructuredFormat = (
    flatPromotions: ExternalGame['labattPromotions']
): ExternalGameForm['labattPromotions'] => {
    const normalizedLabattPromotions: ExternalGameForm['labattPromotions'] = Array.from(
        { length: LABATT_PROMOTION_MAX_LEVEL },
        () => null
    );

    const promotionsByLevel: {
        [level: number]: ExternalGame['labattPromotions'];
    } = {};

    for (const promo of flatPromotions) {
        const level = promo.level;
        if (
            level >= LABATT_PROMOTION_MIN_LEVEL &&
            level <= LABATT_PROMOTION_MAX_LEVEL
        ) {
            if (!promotionsByLevel[level]) {
                promotionsByLevel[level] = [];
            }
            promotionsByLevel[level].push(promo);
        }
    }

    for (
        let level = LABATT_PROMOTION_MIN_LEVEL;
        level <= LABATT_PROMOTION_MAX_LEVEL;
        level++
    ) {
        const promotionsForLevel = promotionsByLevel[level];
        normalizedLabattPromotions[level - LABATT_PROMOTION_MIN_LEVEL] =
            promotionsForLevel && promotionsForLevel.length > 0
                ? promotionsForLevel
                : null;
    }

    return normalizedLabattPromotions;
};

const convertStructuredLabattPromotionsToFlatArray = (
    structuredPromotions: ExternalGameForm['labattPromotions']
): ExternalGame['labattPromotions'] => {
    const result: ExternalGame['labattPromotions'] = [];

    structuredPromotions.forEach((promoArray) => {
        if (promoArray !== null) {
            promoArray.forEach((promo) => {
                if (promo && promo.id) {
                    result.push({
                        ...promo,
                        level: promo.level,
                    });
                }
            });
        }
    });

    return result;
};

const convertExternalGameToForm = (game: ExternalGame): ExternalGameForm => {
    return {
        ...game,
        isFeatured: true,
        labattPromotions: convertFlatLabattPromotionsArrayToStructuredFormat(
            game.labattPromotions
        ),
        provinces: game.provinces.map((province) => ({
            businessUnitId: province.id,
            provinceId: province.province.id,
        })),
        startDate: convertUtcToLocalMoment(game.startDate, game.timezone),
        endDate: convertUtcToLocalMoment(game.endDate, game.timezone),
    };
};

const convertFormToExternalGame = (
    gameForm: ExternalGameForm
): ExternalGameRequestCms => {
    const toUtcStringOrEmpty = (date: Moment | null): string =>
        date && gameForm.timezone
            ? convertLocalToUtcString(date, gameForm.timezone) || ''
            : '';
    const { isFeatured: _isFeatured, ...rest } = gameForm;

    return {
        ...rest,
        startDate: toUtcStringOrEmpty(gameForm.startDate),
        endDate: toUtcStringOrEmpty(gameForm.endDate),
        labattPromotions: convertStructuredLabattPromotionsToFlatArray(
            gameForm.labattPromotions
        ),
    };
};

const YupParams = yup.object({
    action: yup.string().oneOf(['create', 'edit']).required(),
    id: yup
        .string()
        .test('shouldBeDefined', 'Id should be defined', function (v) {
            const action = this.resolve(yup.ref('action'));
            return !(v === undefined && action !== 'create');
        }),
    type: yup.string<GameType>(),
});

type Params = yup.InferType<typeof YupParams>;

export const GameExternalPage = observer(() => {
    const gamesStore = useGamesStore();
    const configStore = useConfigStore();
    const promotionStore = usePromotionStore();
    const params = useParams<Params>();
    const authStore = useAuthStore();
    const navigation = useNavigation();
    const { redirectAfterCreate } = usePlacementLinking();

    const { hasManagementAccess } = authStore;

    if (!hasManagementAccess) {
        navigation.goToHome();
    }

    const [isFetching, setIsFetching] = React.useState<boolean>(false);
    const [isActivateSix12, setIsActivateSix12] = React.useState<boolean>(
        false
    );
    const [checkboxItemErrors, setCheckboxItemErrors] = React.useState<
        CheckboxItemError[]
    >([]);

    const YupGameTranslation = yup.object({
        fr: yup.string().required(),
        en: yup.string().required(),
    });

    const YupGameUrlTranslation = yup.object({
        fr: yup.string().url().required(),
        en: yup.string().url().required(),
    });

    const YupGameFormProvince = yup.object({
        businessUnitId: yup.number().nullable(),
        provinceId: yup.number().required(),
    });

    const YupGameExternalValidationSchema = yup.object({
        title: YupGameTranslation,
        externalUrl: YupGameUrlTranslation,
        six12RegistrationUrl: yup.string().url().notRequired().nullable(),
        provinces: yup
            .array(YupGameFormProvince)
            .required()
            .min(1, 'At least one province must be selected'),
    });

    const formik = useFormik<ExternalGameForm>({
        initialValues: initialExternalGameForm,
        validateOnMount: true,
        validationSchema: YupGameExternalValidationSchema,
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onSubmit: () => {},
    });

    const saveGame = useCallback(async (): Promise<boolean> => {
        formik.values.cmsUserEmail = authStore.claims?.email || null;
        const gameDataWithUtcDates = convertFormToExternalGame(formik.values);

        const visibleCheckboxes = formik.values.checkboxes.filter(
            (cb) => !cb.deletedAt
        );
        const stripHtml = (s: string | undefined | null): string =>
            (s ?? '').replace(/<[^>]*>/g, '').trim();
        const computedCheckboxErrors: CheckboxItemError[] = visibleCheckboxes.map(
            (cb) => {
                const frEmpty = !stripHtml(cb.translations?.fr);
                const enEmpty = !stripHtml(cb.translations?.en);
                if (!frEmpty && !enEmpty) {
                    return { fr: false, en: false };
                }
                let message: string;
                if (frEmpty && enEmpty) {
                    message = 'Text is required in both English and French';
                } else if (enEmpty) {
                    message = 'English text is required';
                } else {
                    message = 'French text is required';
                }
                return { fr: frEmpty, en: enEmpty, message };
            }
        );
        const hasEmptyCheckbox = computedCheckboxErrors.some(
            (err) => err.fr || err.en
        );
        if (hasEmptyCheckbox) {
            setCheckboxItemErrors(computedCheckboxErrors);
            NotificationManager.showError(
                'All checkboxes must have text in both English and French'
            );
            return false;
        }
        setCheckboxItemErrors([]);

        if (isActivateSix12 && formik.values.six12RegistrationUrl == null) {
            NotificationManager.showError(
                'Six12 Registration URL must not be empty'
            );
            return false;
        } else if (
            formik.values.isFeatured &&
            (formik.values.detailImage['en'] == null ||
                formik.values.detailImage['fr'] == null)
        ) {
            NotificationManager.showError(
                'A detail image is mandatory when game is to be featured on homepage'
            );
            return false;
        } else {
            if (params.action === 'edit') {
                await gamesStore.modifyGame(gameDataWithUtcDates);
                return true;
            } else {
                const createdId = await gamesStore.createGame(
                    gameDataWithUtcDates
                );
                return Boolean(createdId);
            }
        }
    }, [
        authStore.claims?.email,
        formik.values,
        gamesStore,
        isActivateSix12,
        params.action,
    ]);

    const { clearDirty } = useUnsavedChanges({
        dirty: formik.dirty,
        onSave: async () => {
            await saveGame();
        },
    });

    const updateGame = useCallback(async () => {
        const success = await saveGame();
        if (success) {
            clearDirty(() =>
                redirectAfterCreate(() => navigation.replaceToGames())
            );
        }
    }, [saveGame, clearDirty, navigation, redirectAfterCreate]);

    const createGame = useCallback(async () => {
        const success = await saveGame();
        if (success) {
            clearDirty(() =>
                redirectAfterCreate(() => navigation.replaceToGames())
            );
        }
    }, [saveGame, clearDirty, navigation, redirectAfterCreate]);

    const onDeleteConfirm = async () => {
        await gamesStore.deleteGame(formik.values.type, formik.values.id);
        navigation.goToGames();
    };

    React.useEffect(() => {
        if (params.action === 'edit') {
            setIsFetching(true);
            gamesStore
                .fetchSingleExternalGame(Number(params.id!))
                .then((game) => {
                    // A contest's game is edited from the contest.
                    if (game.contestId != null) {
                        navigation.replaceToContestEdit(game.contestId);
                        return;
                    }
                    formik.resetForm({
                        values: convertExternalGameToForm(game),
                    });
                    if (game.six12RegistrationUrl) setIsActivateSix12(true);
                })
                .catch(() => navigation.goToGameCreate('external'))
                .finally(() => setIsFetching(false));
        }
    }, [gamesStore]);

    React.useEffect(() => {
        if (formik.values.isForLabatt) {
            promotionStore.fetchLabattPromotions();
        }
    }, [formik.values.isForLabatt]);

    React.useEffect(() => {
        configStore.fetchFeatureFlags();
    }, [configStore]);

    // The type inference is not working well for nested error schema. So we are
    // going to cheat a little bit using lodash
    const checkIfHasError = (path: string) => {
        const hasError = Boolean(get(formik.errors, path));
        const isTouched = Boolean(get(formik.touched, path)); // Typescript is actually correct for the touched object, but I will use lodash.
        return hasError && isTouched;
    };

    const getSelectedPromotionIdsForOtherLevels = (
        levelIdx: number,
        labattPromotions: ExternalGameForm['labattPromotions']
    ): number[] => {
        return labattPromotions.flatMap((promo, idx) => {
            if (idx === levelIdx) return [];
            if (!promo) return [];

            return promo.map((p) => p?.id).filter((id) => !isNil(id));
        });
    };

    const getSelectedPromotionIdsForLevel = (
        levelIdx: number,
        labattPromotions: ExternalGameForm['labattPromotions']
    ): number[] => {
        const promo = labattPromotions[levelIdx];
        if (!promo) return [];

        return promo.map((p) => p?.id).filter((id) => !isNil(id));
    };

    const handleLabattPromotionChange = (
        levelIdx: number,
        selectedPromotionIds: number[]
    ) => {
        const newPromotions = [...formik.values.labattPromotions];
        newPromotions[levelIdx] =
            selectedPromotionIds.length > 0
                ? selectedPromotionIds
                      .map((promotionId: number) => {
                          const promotionFromStore = promotionStore.promotionsLabattForSelect.find(
                              (p) => p.id === promotionId
                          );

                          return promotionFromStore
                              ? {
                                    ...promotionFromStore,
                                    level: levelIdx + 1,
                                }
                              : null;
                      })
                      .filter((promo) => promo !== null)
                : null;
        formik.setFieldValue('labattPromotions', newPromotions);
    };

    const renderPromotionTag = (props: {
        label: React.ReactNode;
        value: number;
        closable: boolean;
        onClose: () => void;
    }) => {
        const { label, value, closable, onClose } = props;

        let promotion = promotionStore.promotionsLabattForSelect.find(
            (p) => p.id === value
        );

        if (!promotion) {
            for (const levelPromotions of formik.values.labattPromotions) {
                if (levelPromotions) {
                    const promotionsArray = Array.isArray(levelPromotions)
                        ? levelPromotions
                        : [levelPromotions];
                    const found = promotionsArray.find((p) => p?.id === value);
                    if (found) {
                        promotion = found;
                        break;
                    }
                }
            }
        }

        const displayTitle = promotion?.title || String(label);

        return (
            <Tag
                onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                }}
                closable={closable}
                onClose={onClose}
                className={style.promotion_tag}
            >
                {displayTitle}
            </Tag>
        );
    };

    const handleBackgroundColorChange = (
        language: 'en' | 'fr',
        value: string | null
    ) => {
        const otherLang = language === 'en' ? 'fr' : 'en';
        const currentBg = formik.values.backgroundColor;
        const currentTitle = formik.values.titleColor;
        const currentSubtitle = formik.values.subtitleColor;
        const currentButton = formik.values.buttonColor;

        if (value !== null) {
            const colorOption = BACKGROUND_COLORS.find(
                (c) => c.value === value
            );
            const otherBgValue = currentBg?.[otherLang] ?? null;
            const otherTitleValue = currentTitle?.[otherLang] ?? null;
            const otherSubtitleValue = currentSubtitle?.[otherLang] ?? null;
            const otherButtonValue = currentButton?.[otherLang] ?? null;

            formik.setFieldValue('backgroundColor', {
                [language]: value,
                [otherLang]: otherBgValue,
            });
            formik.setFieldValue('titleColor', {
                [language]: colorOption?.titleColor ?? null,
                [otherLang]: otherTitleValue,
            });
            formik.setFieldValue('subtitleColor', {
                [language]: colorOption?.subtitleColor ?? null,
                [otherLang]: otherSubtitleValue,
            });
            formik.setFieldValue('buttonColor', {
                [language]: colorOption?.buttonColor ?? null,
                [otherLang]: otherButtonValue,
            });
        } else {
            const otherBgValue = currentBg?.[otherLang] ?? null;
            const otherTitleValue = currentTitle?.[otherLang] ?? null;
            const otherSubtitleValue = currentSubtitle?.[otherLang] ?? null;
            const otherButtonValue = currentButton?.[otherLang] ?? null;

            if (otherBgValue === null) {
                formik.setFieldValue('backgroundColor', null);
                formik.setFieldValue('titleColor', null);
                formik.setFieldValue('buttonColor', null);
            } else {
                formik.setFieldValue('backgroundColor', {
                    [language]: null,
                    [otherLang]: otherBgValue,
                });
                formik.setFieldValue('titleColor', {
                    [language]: null,
                    [otherLang]: otherTitleValue,
                });
                formik.setFieldValue('subtitleColor', {
                    [language]: null,
                    [otherLang]: otherSubtitleValue,
                });
                formik.setFieldValue('buttonColor', {
                    [language]: null,
                    [otherLang]: otherButtonValue,
                });
            }
        }
    };

    const transformId = params.id ? Number(params.id) : undefined;

    const [lang, setLang] = React.useState<AcvtSupportedLanguages>('en');
    const [selectedTab, setSelectedTab] = React.useState<PreviewTabKey>(
        'products'
    );

    const { showCoucheTard, showCircleK } = useDeepLinkVisibility(
        formik.values.provinces
    );

    const deepLinks: DeepLink[] = [];
    if (showCoucheTard) {
        deepLinks.push({
            brand: Brand.CoucheTard,
            url: formik.values.ctDeepLink,
        });
    }
    if (showCircleK) {
        deepLinks.push({
            brand: Brand.CircleK,
            url: formik.values.ckDeepLink,
        });
    }

    const changeLanguage = (language: AcvtSupportedLanguages) => {
        setLang(language);
    };

    const renderTopBarActions = () => {
        switch (params.action) {
            case 'create':
                return (
                    <SaveButton
                        onClick={createGame}
                        loading={gamesStore.isUpsertingGame}
                        disabled={!formik.isValid}
                    />
                );
            case 'edit':
                return (
                    <>
                        <DeleteButton
                            onClick={onDeleteConfirm}
                            loading={gamesStore.isDeletingGame}
                        />
                        <SaveButton
                            onClick={updateGame}
                            loading={gamesStore.isUpsertingGame}
                            disabled={!formik.isValid}
                        />
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <>
            <PageTopBar
                title={
                    params.action === 'edit' && transformId
                        ? `${Text.navigationHeader.game} #${transformId}`
                        : Text.navigationHeader.newGame
                }
                goBack={{
                    callback: () => navigation.goToGames(),
                    text: 'Back to list',
                }}
            >
                {renderTopBarActions()}
            </PageTopBar>
            <div className={`${style.page} content_with_margins`}>
                <form
                    className={`${style.form_container} ${
                        isFetching ? style.hidden : ''
                    }`}
                >
                    <div className={style.config_viewer_split}>
                        <div className={style.left_side}>
                            <h4 className={style.section_header}>Config</h4>
                            <Card>
                                <div className={style.toggles_row}>
                                    <div className={style.single_toggle}>
                                        <IsActiveSwitch
                                            values={formik.values}
                                            title="Active"
                                            onChange={(values) => {
                                                formik.setValues({
                                                    ...formik.values,
                                                    ...values,
                                                });
                                            }}
                                        />
                                    </div>
                                    <div className={style.single_toggle}>
                                        <div
                                            className={
                                                style.toggle_label_with_icon
                                            }
                                        >
                                            <span className="left_label">
                                                Phone Validation Required
                                            </span>
                                        </div>
                                        <Switch
                                            checked={
                                                formik.values
                                                    .phoneNumberValidationRequired ===
                                                true
                                            }
                                            onChange={(e) => {
                                                formik.setFieldValue(
                                                    'phoneNumberValidationRequired',
                                                    e
                                                );
                                                formik.setFieldValue(
                                                    'six12RegistrationUrl',
                                                    null
                                                );
                                                setIsActivateSix12(false);
                                            }}
                                            defaultChecked
                                        />
                                    </div>
                                    <div className={style.single_toggle}>
                                        <div
                                            className={
                                                style.toggle_label_with_icon
                                            }
                                        >
                                            <span className="left_label">
                                                Labatt Game
                                            </span>
                                            <Tooltip
                                                className={style.tooltip}
                                                title="Labatt games require associated promotions for rewards."
                                            >
                                                <InfoCircleOutlined />
                                            </Tooltip>
                                        </div>
                                        <Switch
                                            checked={
                                                formik.values.isForLabatt ===
                                                true
                                            }
                                            onChange={(e) => {
                                                formik.setFieldValue(
                                                    'isForLabatt',
                                                    e
                                                );
                                            }}
                                        />
                                    </div>
                                    <div
                                        className={`${style.single_toggle} ${style.webview_authentication_field}`}
                                    >
                                        <div
                                            className={`${style.toggle_label_with_icon} ${style.webview_authentication_label}`}
                                        >
                                            <span className="left_label">
                                                WebView Authentication
                                            </span>
                                            <Tooltip
                                                className={style.tooltip}
                                                title="Controls which credentials are passed to the game WebView."
                                            >
                                                <InfoCircleOutlined />
                                            </Tooltip>
                                        </div>
                                        <Select<WebViewAuthentication>
                                            className={
                                                style.webview_authentication_select
                                            }
                                            value={
                                                formik.values
                                                    .webviewAuthentication
                                            }
                                            onChange={(value) => {
                                                formik.setFieldValue(
                                                    'webviewAuthentication',
                                                    value
                                                );
                                            }}
                                        >
                                            <Select.Option
                                                value={
                                                    WebViewAuthentication.NONE
                                                }
                                            >
                                                None
                                            </Select.Option>
                                            <Select.Option
                                                value={
                                                    WebViewAuthentication.MOBILE_SESSION
                                                }
                                            >
                                                Mobile app session
                                            </Select.Option>
                                            <Select.Option
                                                value={
                                                    WebViewAuthentication.PARTNER_CODE
                                                }
                                            >
                                                Partner authorization code
                                            </Select.Option>
                                        </Select>
                                    </div>
                                    <div
                                        className={`${style.single_toggle} ${style.feature_flag_field}`}
                                    >
                                        <div
                                            className={`${style.toggle_label_with_icon} ${style.feature_flag_label}`}
                                        >
                                            <span className="left_label">
                                                Feature Flag
                                            </span>
                                            <Tooltip
                                                className={style.tooltip}
                                                title="The game is only shown to users for whom this feature flag is active."
                                            >
                                                <InfoCircleOutlined />
                                            </Tooltip>
                                        </div>
                                        <FeatureFlagSelect
                                            className={
                                                style.feature_flag_select
                                            }
                                            value={
                                                formik.values.featureFlagName
                                            }
                                            flagNames={(
                                                configStore.featureFlags ?? []
                                            ).map((flag) => flag.name)}
                                            loading={
                                                configStore.isFetchingFeatureFlags
                                            }
                                            onChange={(value) => {
                                                formik.setFieldValue(
                                                    'featureFlagName',
                                                    value
                                                );
                                            }}
                                        />
                                    </div>
                                    {formik.values
                                        .phoneNumberValidationRequired && (
                                        <div className={style.single_toggle}>
                                            <span className="left_label">
                                                Activate Six12 Registration
                                            </span>
                                            <Switch
                                                checked={isActivateSix12}
                                                onChange={(e) => {
                                                    formik.setFieldValue(
                                                        'six12RegistrationUrl',
                                                        null
                                                    );
                                                    setIsActivateSix12(e);
                                                }}
                                                defaultChecked
                                            />
                                        </div>
                                    )}
                                </div>
                                {formik.values
                                    .phoneNumberValidationRequired && (
                                    <div className={style.input_row}>
                                        <div
                                            className={
                                                style.fixed_width_input_col
                                            }
                                        >
                                            <ErrorAwareLabel
                                                text={`Registration URL`}
                                                hasError={checkIfHasError(
                                                    'six12RegistrationUrl'
                                                )}
                                            />
                                            <Input
                                                name={`six12RegistrationUrl`}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                value={
                                                    formik.values
                                                        .six12RegistrationUrl
                                                }
                                                disabled={!isActivateSix12}
                                            />
                                        </div>
                                    </div>
                                )}
                            </Card>
                            <div className={style.schedule_section_wrapper}>
                                <h4 className={style.section_header}>
                                    Schedule
                                </h4>
                                <CommonScheduleSection
                                    startDate={formik.values.startDate}
                                    endDate={formik.values.endDate}
                                    timezone={formik.values.timezone}
                                    timezoneField="timezone"
                                    provinces={formik.values.provinces}
                                    setFieldValue={formik.setFieldValue}
                                    checkIfHasError={checkIfHasError}
                                />
                            </div>
                            {formik.values.isForLabatt && (
                                <>
                                    <h4 className={style.section_header}>
                                        Promotion
                                    </h4>
                                    <Card
                                        className={`${style.promotion_card_container}`}
                                    >
                                        <div
                                            className={
                                                style.labatt_promotions_grid
                                            }
                                        >
                                            {[0, 1].map((rowIdx) => (
                                                <div
                                                    className={
                                                        style.labatt_promotions_row
                                                    }
                                                    key={rowIdx}
                                                >
                                                    {[0, 1].map((colIdx) => {
                                                        const levelIdx =
                                                            rowIdx * 2 + colIdx;
                                                        const selectedIds = getSelectedPromotionIdsForOtherLevels(
                                                            levelIdx,
                                                            formik.values
                                                                .labattPromotions
                                                        );

                                                        const promoTooltips = [
                                                            'Should be $2',
                                                            'Should be $4',
                                                            'Should be $10',
                                                            'Should be Corona 0%',
                                                        ];
                                                        return (
                                                            <div
                                                                className={
                                                                    style.input_row
                                                                }
                                                                key={levelIdx}
                                                            >
                                                                <div
                                                                    className={
                                                                        style.promotion_label_select_wrapper
                                                                    }
                                                                >
                                                                    <div
                                                                        className={
                                                                            style.label_with_icon_container
                                                                        }
                                                                    >
                                                                        <div
                                                                            className={
                                                                                style.label_with_icon_container
                                                                            }
                                                                        >
                                                                            <ErrorAwareLabel
                                                                                text={`Promotion Level ${
                                                                                    levelIdx +
                                                                                    1
                                                                                }`}
                                                                                hasError={checkIfHasError(
                                                                                    `labattPromotions.${levelIdx}`
                                                                                )}
                                                                            />
                                                                            <Tooltip
                                                                                title={
                                                                                    promoTooltips[
                                                                                        levelIdx
                                                                                    ]
                                                                                }
                                                                            >
                                                                                <InfoCircleOutlined
                                                                                    className={
                                                                                        style.icon_info
                                                                                    }
                                                                                />
                                                                            </Tooltip>
                                                                        </div>
                                                                    </div>
                                                                    <Select
                                                                        loading={
                                                                            promotionStore.isFetchingForSelect
                                                                        }
                                                                        mode="multiple"
                                                                        value={getSelectedPromotionIdsForLevel(
                                                                            levelIdx,
                                                                            formik
                                                                                .values
                                                                                .labattPromotions
                                                                        )}
                                                                        onChange={(
                                                                            selectedPromotionIds
                                                                        ) => {
                                                                            handleLabattPromotionChange(
                                                                                levelIdx,
                                                                                selectedPromotionIds
                                                                            );
                                                                        }}
                                                                        placeholder={`Select Promotion Level ${
                                                                            levelIdx +
                                                                            1
                                                                        }`}
                                                                        className={
                                                                            style.select_style
                                                                        }
                                                                        optionFilterProp="label"
                                                                        tagRender={
                                                                            renderPromotionTag
                                                                        }
                                                                    >
                                                                        {promotionStore.promotionsLabattForSelect
                                                                            .filter(
                                                                                (
                                                                                    promo
                                                                                ) =>
                                                                                    !selectedIds.includes(
                                                                                        promo.id
                                                                                    )
                                                                            )
                                                                            .map(
                                                                                (
                                                                                    promo
                                                                                ) => (
                                                                                    <Select.Option
                                                                                        key={
                                                                                            promo.id
                                                                                        }
                                                                                        value={
                                                                                            promo.id
                                                                                        }
                                                                                        label={`${
                                                                                            promo.title
                                                                                        } - ${
                                                                                            promo.subTitle ||
                                                                                            ''
                                                                                        }`}
                                                                                    >
                                                                                        <div
                                                                                            className={
                                                                                                style.select_option_container
                                                                                            }
                                                                                        >
                                                                                            {promo.thumbnail && (
                                                                                                <img
                                                                                                    src={
                                                                                                        promo.thumbnail
                                                                                                    }
                                                                                                    alt={
                                                                                                        promo.title
                                                                                                    }
                                                                                                    className={
                                                                                                        style.select_option_image
                                                                                                    }
                                                                                                />
                                                                                            )}
                                                                                            <div>
                                                                                                <div
                                                                                                    className={
                                                                                                        style.select_option_title
                                                                                                    }
                                                                                                >
                                                                                                    {
                                                                                                        promo.title
                                                                                                    }
                                                                                                </div>
                                                                                                {promo.subTitle && (
                                                                                                    <div
                                                                                                        className={
                                                                                                            style.select_option_subtitle
                                                                                                        }
                                                                                                    >
                                                                                                        {
                                                                                                            promo.subTitle
                                                                                                        }
                                                                                                    </div>
                                                                                                )}
                                                                                            </div>
                                                                                        </div>
                                                                                    </Select.Option>
                                                                                )
                                                                            )}
                                                                    </Select>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ))}
                                        </div>
                                    </Card>
                                </>
                            )}
                            {/* Images Section */}
                            <Card>
                                <Images
                                    image={formik.values.image}
                                    title="Featured Image"
                                    enableCrop={true}
                                    allowSkipCrop={true}
                                    aspectRatio={305 / 180}
                                    onChange={(image) => {
                                        formik.setValues({
                                            ...formik.values,
                                            image,
                                        });
                                    }}
                                    onDelete={(image) => {
                                        formik.setValues({
                                            ...formik.values,
                                            image,
                                        });
                                    }}
                                />
                                <Images
                                    image={formik.values.detailImage}
                                    title="Detail View Image"
                                    enableCrop={true}
                                    allowSkipCrop={true}
                                    aspectRatio={305 / 180}
                                    onChange={(detailImage) => {
                                        formik.setValues({
                                            ...formik.values,
                                            detailImage,
                                        });
                                    }}
                                    onDelete={(detailImage) => {
                                        formik.setValues({
                                            ...formik.values,
                                            detailImage,
                                        });
                                    }}
                                />
                            </Card>
                            {/* Background Color Section */}
                            <Section title="Background Color">
                                <div className={style.input_row}>
                                    {SupportedLanguages.map((language) => (
                                        <div
                                            key={language}
                                            className={
                                                style.fixed_width_input_col
                                            }
                                        >
                                            <span
                                                className={style.language_label}
                                            >
                                                {localizeLanguage(language)}
                                            </span>
                                            <BackgroundColorPicker
                                                value={
                                                    formik.values
                                                        .backgroundColor?.[
                                                        language
                                                    ] ?? null
                                                }
                                                onChange={(value) =>
                                                    handleBackgroundColorChange(
                                                        language,
                                                        value
                                                    )
                                                }
                                                language={language}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </Section>
                            {/* Details Section */}
                            <Card style={{ marginTop: '1rem' }}>
                                <div>
                                    <GameForm
                                        values={formik.values}
                                        onChange={(values) => {
                                            formik.setValues({
                                                ...formik.values,
                                                ...values,
                                            });
                                        }}
                                        errors={{
                                            fr: checkIfHasError('fr'),
                                            en: checkIfHasError('en'),
                                        }}
                                    />
                                    <div className={style.input_row}>
                                        {SupportedLanguages.map((lang) => (
                                            <div
                                                key={lang}
                                                className={
                                                    style.fixed_width_input_col
                                                }
                                            >
                                                <ErrorAwareLabel
                                                    text={`External URL ${lang}`}
                                                    hasError={checkIfHasError(
                                                        `externalUrl.${lang}`
                                                    )}
                                                />
                                                <Input
                                                    name={`externalUrl.${lang}`}
                                                    onChange={
                                                        formik.handleChange
                                                    }
                                                    onBlur={formik.handleBlur}
                                                    value={
                                                        formik.values
                                                            .externalUrl[lang]
                                                    }
                                                />
                                            </div>
                                        ))}
                                    </div>
                                    <div
                                        className={
                                            style.details_cards_container
                                        }
                                    >
                                        <CheckboxForm
                                            values={formik.values}
                                            onChange={(values) => {
                                                formik.setValues({
                                                    ...formik.values,
                                                    ...values,
                                                });
                                                if (
                                                    checkboxItemErrors.length >
                                                    0
                                                ) {
                                                    setCheckboxItemErrors([]);
                                                }
                                            }}
                                            errors={{
                                                fr: checkIfHasError('fr'),
                                                en: checkIfHasError('en'),
                                            }}
                                            itemErrors={checkboxItemErrors}
                                        />
                                    </div>
                                </div>
                            </Card>
                            {isFetching && <Spin />}
                        </div>
                        <div className={style.preview_container}>
                            <h4 className={style.section_header}>
                                App Preview
                            </h4>
                            <PreviewProvider
                                key={lang}
                                initialRegion={ApplicationTargetEnum.QC}
                                initialLanguage={lang}
                            >
                                <PreviewThemeProvider
                                    region={ApplicationTargetEnum.QC}
                                >
                                    <AcvtCanvas changeLang={changeLanguage}>
                                        <Acvt>
                                            <AcvtGamePage
                                                key={formik.values.id}
                                                gameObject={{
                                                    ...convertFormToExternalGame(
                                                        formik.values
                                                    ),
                                                    isFeatured: true,
                                                }}
                                            />
                                            <PreviewBottomTabBar
                                                selectedTab={selectedTab}
                                                onTabChange={setSelectedTab}
                                                readOnly
                                            />
                                        </Acvt>
                                    </AcvtCanvas>
                                </PreviewThemeProvider>
                            </PreviewProvider>
                            <div style={{ marginTop: 35 }}>
                                <DeepLinkSection links={deepLinks} />
                            </div>
                        </div>
                    </div>
                </form>

                {formik.values.cmsUserHistory &&
                    formik.values.cmsUserHistory.length > 0 && (
                        <CmsUserHistoryBar
                            cmsUserHistory={formik.values.cmsUserHistory}
                        />
                    )}
            </div>
        </>
    );
});
